import React, { useMemo, useState, useEffect, useCallback } from 'react';
import OrdersFilters from './OrdersFilters';
import OrdersTable from './OrdersTable';
import OrdersMobileView from './OrdersMobileView';
import { API_BASE_URL } from '../../config';
import { formatDateMoscow } from '../../utils/dateUtils';

const PAGE_SIZE = 50;

const OrdersPage = ({ onExport, orderSource = null, title = 'Управление заказами', embedded = false }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all'); // all | automatic | manual
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, searchTerm, typeFilter, orderSource]);

    const applySearch = () => {
        setSearchTerm(searchInput.trim());
    };

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page.toString(),
                page_size: PAGE_SIZE.toString(),
            });
            if (statusFilter !== 'all') {
                params.append('status', statusFilter);
            }
            if (typeFilter !== 'all') {
                params.append('order_type', typeFilter);
            }
            if (searchTerm) {
                params.append('search', searchTerm);
            }
            if (orderSource) {
                params.append('order_source', orderSource);
            }
            const response = await fetch(`${API_BASE_URL}/api/orders/?${params.toString()}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const formattedOrders = (data.items || []).map(order => {
                const amount = order.paid_amount != null ? order.paid_amount : order.amount;
                const kw = order.campaign_keyword || '';
                const isFrog = kw.startsWith('frog:');
                const displayUsername = isFrog
                    ? kw.slice(5)
                    : (order.user_username || 'Неизвестно');
                const typeLabel = order.order_type === 'manual' ? 'Ручной' : 'Авто';
                const recipientDisplay = order.recipient === 'friend'
                    ? `@${order.recipient_username || '—'}`
                    : 'Себе';
                const pk = order.purchase_kind || 'stars';
                const productCell = pk === 'digital_good'
                    ? (order.digital_product_title || 'Цифровой товар')
                    : `${order.quantity} ⭐`;
                const purchaseKindLabel = pk === 'digital_good' ? 'Цифровой' : 'Звёзды';
                const orderSource = order.order_source || 'bot';
                const sourceLabels = {
                    bot: order.referral_bot_username ? `@${order.referral_bot_username}` : 'Основной бот',
                    webapp: 'WebApp',
                    website: 'Сайт',
                    referral_bot: order.referral_bot_username ? `@${order.referral_bot_username}` : 'Партнёрский бот',
                };
                return {
                    id: order.id.toString(),
                    userId: order.user_id,
                    username: displayUsername,
                    recipient: recipientDisplay,
                    stars: order.quantity,
                    productCell,
                    purchaseKindLabel,
                    amount: parseFloat(amount).toFixed(2),
                    status: order.status,
                    source: sourceLabels[orderSource] || sourceLabels.bot,
                    date: formatDateMoscow(order.created_at),
                    type: typeLabel,
                    digitalCodes: order.digital_codes || null,
                    digitalRedeemText: order.digital_redeem_text || null,
                    paymentId: order.payment_id || null,
                    manualReviewStatus: order.manual_review_status || null,
                    manualReviewByUserId: order.manual_review_by_user_id || null,
                    manualReviewAt: order.manual_review_at || null,
                    manualReviewComment: order.manual_review_comment || null,
                };
            });
            setOrders(formattedOrders);
            setTotal(data.total ?? 0);
            setTotalPages(data.total_pages ?? 1);
            setError(null);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('Не удалось загрузить заказы');
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter, typeFilter, searchTerm, orderSource]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleManualReviewDecision = async (order, action) => {
        const comment = window.prompt(
            action === 'accept'
                ? 'Комментарий к подтверждению (необязательно)'
                : 'Причина отклонения (необязательно)',
            '',
        );
        if (comment === null) return;
        const response = await fetch(`${API_BASE_URL}/api/orders/${order.id}/manual-review/${action}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ comment }),
        });
        if (!response.ok) {
            setError('Не удалось сохранить решение по ручной проверке');
            return;
        }
        await fetchOrders();
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const filteredOrders = useMemo(() => {
        let result = [...orders];
        if (sortConfig.key) {
            result.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return result;
    }, [orders, sortConfig]);

    if (loading) {
        return (
            <div className={embedded ? 'space-y-4' : 'space-y-6'}>
                {!embedded && (
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
                    </div>
                )}
                <div className="flex items-center justify-center py-12">
                    <div className="text-slate-500">Загрузка заказов...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={embedded ? 'space-y-4' : 'space-y-6'}>
                {!embedded && (
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
                    </div>
                )}
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-rose-600">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className={embedded ? 'space-y-4' : 'space-y-6'}>
            {!embedded && (
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h2 className="text-2xl font-bold text-slate-800">{title}</h2>
                </div>
            )}
            {embedded && (
                <div>
                    <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
                    <p className="mt-1 text-sm text-slate-500">Все периоды · все статусы · новые сверху</p>
                </div>
            )}

            <OrdersFilters
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                onSearch={applySearch}
                searchTerm={searchTerm}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
            />

            <OrdersTable
                orders={filteredOrders}
                sortConfig={sortConfig}
                onSort={handleSort}
                onManualReviewDecision={handleManualReviewDecision}
            />

            <OrdersMobileView orders={filteredOrders} onManualReviewDecision={handleManualReviewDecision} />

            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                        Назад
                    </button>
                    <span className="px-4 py-2 text-slate-600">
                        Страница {page} из {totalPages} (всего {total})
                    </span>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                        Вперед
                    </button>
                </div>
            )}
        </div>
    );
};

export default OrdersPage;
